import { GridAlpha, TextInput, DatePicker, Select, Combobox } from '@applyboard/crystal-ui';
import { CountryIsoCode } from '~/constants';
import { useGradeSchemas } from '~/data/useGradeSchemas';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import {
  type EducationHistoryEditData,
  type GradeSchemeConfig,
  safeDateValue,
  getEducationDateBounds,
  EDUCATION_LEVEL_OPTIONS,
  COUNTRY_OPTIONS,
} from './EducationHistoryEditFields';

const { minDate: EDUCATION_MIN_DATE, maxDate: EDUCATION_MAX_DATE } = getEducationDateBounds();

const GRADE_SCHEME_DEFAULTS: GradeSchemeConfig = { enabled: false, required: false };

type CreateEducationHistoryFieldsProps = Readonly<{
  editedData: EducationHistoryEditData;
  onChange: (field: keyof EducationHistoryEditData, value: string) => void;
  gradeScheme?: GradeSchemeConfig;
}>;

/**
 * Form fields for the Add Education History dialog.
 * Same layout as EducationHistoryEditFields but without Degree and Field of Study.
 */
export function CreateEducationHistoryFields({
  editedData,
  onChange,
  gradeScheme = GRADE_SCHEME_DEFAULTS,
}: CreateEducationHistoryFieldsProps) {
  const { gradeSchemas } = useGradeSchemas();
  const { isMobile } = useMediaQuery();

  const gradeSchemeOptions = gradeSchemas.map((schema) => ({
    value: schema.id,
    label: schema.name,
  }));

  const columns = isMobile ? '1fr' : 'repeat(3, 1fr)';
  const fieldCount = gradeScheme.enabled ? 7 : 6;
  const rowCount = isMobile ? fieldCount : Math.ceil(fieldCount / 3);

  return (
    <GridAlpha rows={`repeat(${rowCount}, auto)`} columnGap={2} rowGap={6} columns={columns}>
      <GridAlpha.Item>
        <Select
          label="Education level"
          value={editedData.educationLevel}
          onChange={(value: string) => onChange('educationLevel', value)}
          appearance="styled"
          required
        >
          {EDUCATION_LEVEL_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value} label={option.label} />
          ))}
        </Select>
      </GridAlpha.Item>
      <GridAlpha.Item>
        <Combobox
          label="Education country"
          value={editedData.educationCountry}
          onChange={(value: string) => onChange('educationCountry', value as CountryIsoCode)}
          required
        >
          {COUNTRY_OPTIONS.map((option) => (
            <Combobox.Option key={option.value} value={option.value} label={option.label} />
          ))}
        </Combobox>
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput
          label="Name of institution"
          value={editedData.schoolName}
          onChange={(value: string) => onChange('schoolName', value)}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput
          label="Credential gained (or expected)"
          value={editedData.credentialReceived}
          onChange={(value: string) => onChange('credentialReceived', value)}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <DatePicker
          label="Education start"
          value={safeDateValue(editedData.startDate)}
          onChange={(value: string) => onChange('startDate', value)}
          granularity="month"
          minDate={EDUCATION_MIN_DATE}
          maxDate={EDUCATION_MAX_DATE}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <DatePicker
          label="Education end (or expected)"
          value={safeDateValue(editedData.endDate)}
          onChange={(value: string) => onChange('endDate', value)}
          granularity="month"
          minDate={EDUCATION_MIN_DATE}
          maxDate={EDUCATION_MAX_DATE}
          required
        />
      </GridAlpha.Item>
      {gradeScheme.enabled && (
        <GridAlpha.Item>
          <Select
            label="Grade scheme"
            value={editedData.gradeSchemeId}
            onChange={(value: string) => {
              onChange('gradeSchemeId', value);
              const selected = gradeSchemeOptions.find((opt) => opt.value === value);
              if (selected) {
                onChange('gradeSchemeName', selected.label);
              } else {
                onChange('gradeSchemeName', '');
              }
            }}
            appearance="styled"
            required={gradeScheme.required}
          >
            {gradeSchemeOptions.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label} />
            ))}
          </Select>
        </GridAlpha.Item>
      )}
    </GridAlpha>
  );
}
